import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Navbar from "../../components/layout/Navbar";
import Footer from "../../components/layout/Footer";
import CancelSubscriptionModal from "../../components/modals/CancelSubscriptionModal";
import Toast from "../../components/ui/Toast";
import {
  fetchPurchaseHistory,
  cancelSubscription,
} from "../../redux/subscription";
import "../../components/styles/subscriptionPage.scss";

/**
 * SubscriptionPage — Subscription management
 * Figma: Subscription (Desktop) (24654:6090) — 1920×1200
 *
 * Now wired to real CRM API:
 *   - Fetches purchase history on mount via fetchPurchaseHistory()
 *   - Shows active subscription details from Redux
 *   - Billing history from real orders
 *   - Cancel subscription via CRM two-step flow
 */

const TABS = ["Settings", "Subscription", "Parental"];

const TAB_ROUTES = {
  Settings: "/settings",
  Subscription: "/settings/subscription",
  Parental: "/settings/parental",
};

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("Subscription");
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [toast, setToast] = useState({ visible: false, message: "", type: "success" });
  // When a billing history row is clicked, its order replaces activeSubscription in plan details
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Redux state
  const {
    activeSubscription,
    hasActiveSubscription,
    purchaseHistory,
    historyLoading,
    isLoading,
  } = useSelector((state) => state.subscription);

  // Fetch billing data on mount — "All" returns active + cancelled + expired so
  // cancelled subscriptions still appear in the history table.
  useEffect(() => {
    fetchPurchaseHistory("All");
  }, []);

  // Plan details display source: selected history row (if any), else active subscription
  const planSource = selectedOrder || activeSubscription;

  // Extract plan details
  const planName = planSource?.product_name ||
    planSource?.name ||
    planSource?.title ||
    "No active plan";
  const planPrice = planSource?.price
    ? `${planSource.currency || "NGN"} ${planSource.price}`
    : "—";
  const nextBilling = planSource?.end_date
    ? new Date(planSource.end_date).toLocaleDateString("en-GB")
    : "—";
  const productId =
    planSource?.product_id || planSource?.id;
  // Only allow cancellation for an actively-running subscription (not historical orders)
  const isPlanActive = selectedOrder
    ? (selectedOrder.status === "Active")
    : hasActiveSubscription;

  // Format billing history from purchase history (exclude zero-amount/free plans)
  // Keep the full `order` on each row so we can restore it into plan details on click.
  const billingRows = (purchaseHistory || [])
    .filter((order) => {
      const price = parseFloat(order.price || order.store_price || 0);
      return price > 0;
    })
    .map((order, i) => ({
      no: i + 1,
      order,
      plan: order.product_name || order.name || "Plan",
      date: order.created_at
        ? new Date(order.created_at).toLocaleDateString("en-GB")
        : order.start_date
          ? new Date(order.start_date).toLocaleDateString("en-GB")
          : "—",
      amount: order.price
        ? `${order.currency || "NGN"} ${order.price}`
        : "—",
      method: order.medium || order.payment_method || "Web",
      status: order.status || "Unknown",
    }));

  const handleCancelSubscription = async (reason) => {
    if (!productId) {
      setToast({
        visible: true,
        message: "Unable to cancel: no product ID found. Try refreshing the page.",
        type: "error",
      });
      return;
    }
    console.log("[SubscriptionPage] Cancelling:", { productId, reason });
    const result = await cancelSubscription(productId, reason);
    if (result.success) {
      setShowCancelModal(false);
      // Clear the locally-selected order so plan details no longer shows the
      // cancelled subscription — otherwise stale local state overrides Redux.
      setSelectedOrder(null);
      setToast({
        visible: true,
        message: "Subscription cancelled",
        type: "success",
      });
      // History already refreshed inside cancelSubscription
    } else {
      setToast({
        visible: true,
        message: result.error || "Cancellation failed. Please try again.",
        type: "error",
      });
    }
  };

  const hideToast = () => setToast((t) => ({ ...t, visible: false }));

  return (
    <div className="subscription-page">
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.visible}
        onClose={hideToast}
      />
      <Navbar activeTab="" />

      <div className="subscription-page__body">
        <h1 className="subscription-page__title">Subscription</h1>

        {/* Tab bar */}
        <div className="subscription-page__tabs">
          {TABS.map((tab) => (
            <button
              key={tab}
              className={`subscription-page__tab ${
                activeTab === tab ? "subscription-page__tab--active" : ""
              }`}
              onClick={() => {
                setActiveTab(tab);
                navigate(TAB_ROUTES[tab]);
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Plan details */}
        <div className="subscription-page__card">
          <h3 className="subscription-page__card-title">Plan details</h3>

          {historyLoading ? (
            <div
              style={{
                padding: "20px 0",
                color: "#a3a3a3",
                fontSize: "14px",
              }}
            >
              Loading subscription details...
            </div>
          ) : (
            <div className="subscription-page__plan-grid">
              <div className="subscription-page__plan-item">
                <span className="subscription-page__plan-label">
                  Current plan
                </span>
                <span className="subscription-page__plan-value">
                  {planName}
                </span>
              </div>
              <div className="subscription-page__plan-item">
                <span className="subscription-page__plan-label">Price</span>
                <span className="subscription-page__plan-value">
                  {planPrice}
                </span>
              </div>
              <div className="subscription-page__plan-item">
                <span className="subscription-page__plan-label">
                  Next billing date
                </span>
                <span className="subscription-page__plan-value">
                  {nextBilling}
                </span>
              </div>
              <div className="subscription-page__plan-item">
                <span className="subscription-page__plan-label">
                  Status
                </span>
                <span className="subscription-page__plan-value">
                  {isPlanActive ? (
                    <span style={{ color: "#4ade80" }}>Active</span>
                  ) : (
                    <span style={{ color: "#a3a3a3" }}>
                      {selectedOrder ? (selectedOrder.status || "Inactive") : "No active plan"}
                    </span>
                  )}
                </span>
              </div>
              <div className="subscription-page__plan-actions">
                {isPlanActive ? (
                  <button
                    className="subscription-page__cancel-btn"
                    onClick={() => setShowCancelModal(true)}
                    disabled={isLoading}
                  >
                    {isLoading ? "Processing..." : "Cancel subscription"}
                  </button>
                ) : (
                  selectedOrder && (
                    <button
                      className="subscription-page__cancel-btn"
                      onClick={() => setSelectedOrder(null)}
                    >
                      Back to active plan
                    </button>
                  )
                )}
              </div>
            </div>
          )}
        </div>

        {/* Billing history */}
        <div className="subscription-page__card">
          <h3 className="subscription-page__card-title">Billing history</h3>

          {billingRows.length > 0 ? (
            <table className="subscription-page__table">
              <thead>
                <tr>
                  <th>No.</th>
                  <th>Plan</th>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Payment Method</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {billingRows.map((row) => {
                  const isSelected = selectedOrder && (selectedOrder.id === row.order.id || selectedOrder.product_id === row.order.product_id);
                  return (
                    <tr
                      key={row.no}
                      onClick={() => {
                        console.log("[SubscriptionPage] History row clicked:", row.plan, row.order);
                        setSelectedOrder(row.order);
                      }}
                      style={{ cursor: "pointer", background: isSelected ? "rgba(252, 152, 43, 0.08)" : undefined }}
                    >
                      <td>{row.no}.</td>
                      <td>{row.plan}</td>
                      <td>{row.date}</td>
                      <td>{row.amount}</td>
                      <td>{row.method}</td>
                      <td>
                        <span
                          className={`subscription-page__status-badge ${
                            row.status === "Active"
                              ? "subscription-page__status-badge--active"
                              : ""
                          }`}
                        >
                          {row.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div
              style={{
                padding: "20px 0",
                color: "#a3a3a3",
                fontSize: "14px",
              }}
            >
              {historyLoading
                ? "Loading billing history..."
                : "No billing history yet."}
            </div>
          )}
        </div>
      </div>

      <Footer />

      {/* Cancel subscription modal */}
      {showCancelModal && (
        <CancelSubscriptionModal
          isOpen={showCancelModal}
          onClose={() => setShowCancelModal(false)}
          onConfirm={handleCancelSubscription}
          userName={localStorage.getItem("mango_username") || ""}
          endDate={
            activeSubscription?.end_date
              ? new Date(activeSubscription.end_date).toLocaleDateString("en-GB", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })
              : ""
          }
          email={localStorage.getItem("mango_email") || ""}
        />
      )}
    </div>
  );
};

export default SubscriptionPage;
